import React, { useCallback, useMemo, useRef } from 'react'
import useScrollPosition from '@react-hook/window-scroll'
import { Text } from 'rebass'
import { NavLink, useHistory, useLocation } from 'react-router-dom'
import { darken } from 'polished'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components/macro'

import { useActiveWeb3React } from '../../hooks/web3'
import { useETHBalances } from '../../state/wallet/hooks'

import Row, { RowFixed } from '../Row'
import Web3Status from '../Web3Status'
import { IconClose32, IconDirection12, IconH5Menu32, IconXterioLogoScalable } from 'xterio-icons'
import { useModalOpen, useToggleModal, useWalletModalToggle } from 'state/application/hooks'
import { ApplicationModal, WalletModalTriggerSource } from 'state/application/actions'
import { useOnClickOutside } from 'hooks/useOnClickOutside'
import { NETWORK_LABELS } from 'constants/wallet'
import { HideExtraSmall } from 'theme'

const HeaderFrame = styled.div<{ showBackground: boolean }>`
  width: 100%;
  display: flex;
  flex-direction: row;
  align-items:center;
  justify-content: space-between;
  padding: 0px 60px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    padding: 0px 20px;
  `};
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    padding: 0 20px;
  `};

  /* Background slide effect on scroll. */
  background-image: ${({ theme }) => `linear-gradient(to bottom, transparent 50%, ${theme.bg0} 50% )}}`}
  background-position: ${({ showBackground }) => (showBackground ? '0 -100%' : '0 0')};
  background-size: 100% 200%;
  box-shadow: 0px 0px 0px 1px ${({ theme, showBackground }) => (showBackground ? theme.bg2 : 'transparent;')};
  transition: background-position .1s, box-shadow .1s;
`

const HeaderControls = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-self: flex-end;
`

const HeaderElement = styled.div`
  display: flex;
  align-items: center;
`

const HeaderRow = styled(RowFixed)`
  ${({ theme }) => theme.mediaWidth.upToMedium`
   width: 100%;
  `};
`

const HeaderLinks = styled(Row)`
  ${({ theme }) => theme.mediaWidth.upToSmall`
    display: none;
  `};
`

const AccountElement = styled.div<{ active: boolean }>`
  display: flex;
  flex-direction: row;
  align-items: center;
  background-color: ${({ theme, active }) => (!active ? theme.bg1 : theme.bg2)};
  border-radius: 100px;
  white-space: nowrap;
  width: 100%;
  cursor: pointer;
  margin-left: 8px;

  :focus {
    border: 1px solid blue;
  }
`

const NetworkCard = styled.div`
  height: 40px;
  display: flex;
  flex-direction: row;
  align-items: center;
  border-radius: 24px;
  padding: 8px;
  padding-right: 16px;
  white-space: pre;
  font-size: 14px;
  background-color: ${({ theme }) => theme.bg02};
  color: ${({ theme }) => theme.text1};
  cursor: pointer;
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    height: 32px;
    width: 32px;
    padding: 4px;
  `};
`

const BalanceText = styled(Text)`
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    display: none;
  `};
`

const Title = styled.a`
  display: flex;
  align-items: center;
  pointer-events: auto;
  justify-self: flex-start;
  margin-right: 12px;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    justify-self: center;
  `};
  :hover {
    cursor: pointer;
  }
`

const activeClassName = 'ACTIVE'

const StyledNavLink = styled(NavLink).attrs({
  activeClassName,
})`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: left;
  border-radius: 3rem;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.text60};
  font-size: 1rem;
  width: fit-content;
  font-weight: 500;
  padding: 8px 12px;

  &.${activeClassName} {
    border-radius: 12px;
    font-weight: 600;
    color: ${({ theme }) => theme.text1};
    // background-color: ${({ theme }) => theme.bg2};
  }

  :hover,
  :focus {
    color: ${({ theme }) => darken(0.1, theme.text1)};
  }
`

const StyledNavLinkExternal = styled.a`
  ${({ theme }) => theme.flexRowNoWrap}
  align-items: left;
  border-radius: 3rem;
  outline: none;
  cursor: pointer;
  text-decoration: none;
  color: ${({ theme }) => theme.text60};
  font-size: 1rem;
  width: fit-content;
  font-weight: 500;
  padding: 8px 12px;
  display: none;

  :hover {
    color: ${({ theme }) => darken(0.1, theme.text1)};
  }
`

const Logo = styled(IconXterioLogoScalable)`
  width: 132px;
  height: 27px;
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    width: 97px;
    height: 20px;
  `};
`

export default function Header() {
  const { account, chainId } = useActiveWeb3React()
  const { t } = useTranslation()

  const userEthBalance = useETHBalances(account ? [account] : [])?.[account ?? '']
  // const [isDark] = useDarkModeManager()

  const scrollY = useScrollPosition()
  const toggleWalletModal = useWalletModalToggle()
  const curChain = useMemo(() => (chainId ? NETWORK_LABELS[chainId] : null), [chainId])
  const { icon: Icon } = curChain || {}
  return (
    <HeaderFrame showBackground={scrollY > 45}>
      <HeaderRow>
        <Title href="." style={{ marginRight: 64, color: '#ffffff' }}>
          <Logo />
        </Title>

        <HeaderLinks>
          <StyledNavLink id={`swap-nav-link`} to={'/swap'}>
            {t('swap')}
          </StyledNavLink>
          <StyledNavLink
            id={`pool-nav-link`}
            to={'/pool'}
            isActive={(match, { pathname }) =>
              Boolean(match) ||
              pathname.startsWith('/add') ||
              pathname.startsWith('/remove') ||
              pathname.startsWith('/increase') ||
              pathname.startsWith('/find')
            }
          >
            {t('pool')}
          </StyledNavLink>
          <StyledNavLinkExternal
            id={`bridge-nav-link`}
            href="https://bnb-bridge.xter.io"
            target="_blank"
            rel="noreferrer"
          >
            {t('bridge')}
            <IconDirection12 style={{ rotate: '-135deg', marginTop: 4 }} />
          </StyledNavLinkExternal>
        </HeaderLinks>
      </HeaderRow>
      <HeaderControls>
        <HeaderElement>
          {curChain && (
            <NetworkCard onClick={() => toggleWalletModal(WalletModalTriggerSource.chain)}>
              {Icon ? <Icon size={24} /> : null}
              <HideExtraSmall>
                <span style={{ marginLeft: 8, marginRight: 4 }}>{curChain?.name}</span>
                {/* <SvgIcon name={'Refresh'} size={12} /> */}
              </HideExtraSmall>
            </NetworkCard>
          )}
          <AccountElement active={!!account} style={{ pointerEvents: 'auto' }}>
            {account && userEthBalance ? (
              <BalanceText style={{ flexShrink: 0 }} pl="0.75rem" pr="0.5rem" fontWeight={500}>
                {userEthBalance?.toSignificant(4)} BNB
              </BalanceText>
            ) : null}
            <Web3Status />
          </AccountElement>
        </HeaderElement>
        <HeaderMenu />
      </HeaderControls>
    </HeaderFrame>
  )
}

const StyledMenu = styled.div`
  margin-left: 0.5rem;
  display: none;
  justify-content: center;
  align-items: center;
  position: relative;
  border: none;
  text-align: left;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    display: flex;
  `};
`
const StyledMenuButton = styled.button`
  width: 40px;
  height: 40px;
  border: none;
  background-color: transparent;
  margin: 0;
  padding: 0;
  background-color: ${({ theme }) => theme.bg02};
  border-radius: 50%;
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    width: 32px;
    height: 32px;
  `};

  :hover,
  :focus {
    cursor: pointer;
    outline: none;
    background-color: ${({ theme }) => darken(0.05, theme.bg02)};
  }

  svg {
    margin-top: 2px;
  }
`
const MenuFlyout = styled.span`
  background-color: ${({ theme }) => theme.bg01};
  border: solid 1px ${({ theme }) => theme.text10};
  box-shadow: 0px 0px 1px rgba(0, 0, 0, 0.01), 0px 4px 8px rgba(0, 0, 0, 0.04), 0px 16px 24px rgba(0, 0, 0, 0.04),
    0px 24px 32px rgba(0, 0, 0, 0.01);
  border-radius: 12px;
  padding: 16px;
  display: flex;
  flex-direction: column;
  font-size: 16px;
  gap: 8px;
  position: fixed;
  top: 88px;
  left: 60px;
  right: 60px;
  z-index: 100;
  ${({ theme }) => theme.mediaWidth.upToSmall`
    right: 20px;
    left: 20px;
  `};
  ${({ theme }) => theme.mediaWidth.upToExtraSmall`
    top: 64px;
  `};
`
const MenuItem = styled.div<{ isActive?: boolean }>`
  height: 40px;
  line-height: 40px;
  color: ${({ theme, isActive }) => (isActive ? theme.white : theme.text60)};
  :hover {
    color: ${({ theme }) => theme.white};
    cursor: pointer;
    text-decoration: none;
  }
`

function HeaderMenu() {
  const { t } = useTranslation()
  const history = useHistory()
  const pathname = useLocation()?.pathname // 获取当前的 pathname
  const node = useRef<HTMLDivElement>()
  const open = useModalOpen(ApplicationModal.MENU)
  const toggle = useToggleModal(ApplicationModal.MENU)
  useOnClickOutside(node, open ? toggle : undefined)

  const [isActivePool, isActiveSwap] = useMemo(() => {
    return [
      pathname === '/pool' ||
        pathname.startsWith('/add') ||
        pathname.startsWith('/remove') ||
        pathname.startsWith('/increase') ||
        pathname.startsWith('/find'),
      pathname === '/swap',
    ]
  }, [pathname])
  const onMenuPress = useCallback(
    (p: string) => {
      history.push(p)
      toggle()
    },
    [history, toggle]
  )

  return (
    // https://github.com/DefinitelyTyped/DefinitelyTyped/issues/30451
    <StyledMenu ref={node as any}>
      <StyledMenuButton onClick={toggle}>
        {!open ? <IconH5Menu32 color="white" size="16" /> : <IconClose32 color="white" size="16" />}
      </StyledMenuButton>

      {open && (
        <MenuFlyout>
          <MenuItem isActive={isActiveSwap} onClick={() => onMenuPress('/swap')}>
            {t('swap')}
          </MenuItem>
          <MenuItem isActive={isActivePool} onClick={() => onMenuPress('/pool')}>
            {t('pool')}
          </MenuItem>
          {/* <MenuItem isActive={false} onClick={() => window.open('https://bnb-bridge.xter.io', '_blank')}>
            {t('bridge')}
            <IconDirection12 style={{ rotate: '-135deg', marginTop: 4 }} />
          </MenuItem> */}
        </MenuFlyout>
      )}
    </StyledMenu>
  )
}
